<template>
  <q-dialog ref="dialogRef" persistent @hide="onDialogHide">
    <q-card
      :style="{ minWidth: '600px' }"
      class="relative-position elevation-1"
    >
      <!-- Header -->
      <q-card-section
        class="flex justify-between items-center q-px-2x-md q-py-xl"
      >
        <span class="text-lg text-neutral text-weight-medium">
          {{ heading }}
        </span>

        <PBtn flat dense round size="sm" @click="onDialogCancel">
          <CloseIcon class="icon" />
        </PBtn>
      </q-card-section>

      <q-separator />

      <q-card-section class="q-px-2x-md q-py-xl">
        <q-form class="q-gutter-y-md" @submit="onSubmit">
          <PSelectInput
            v-model="formData.type"
            label="Type"
            placeholder="Select Type"
            :options="processedCommunicationTypes"
            :options-loading="unref(communicationTypesFilter.loading)"
            option-label="name"
            option-value="_id"
            use-chips
            multiple
            clearable
            required
            :validations="{ required: true }"
            @update:model-value="handleTypeSelection"
            @filter="communicationTypesFilter.onFilter"
            @filter-abort="communicationTypesFilter.onFilterAbort"
            @virtual-scroll="communicationTypesFilter.onScroll"
          >
          </PSelectInput>

          <PSelectInput
            v-model="recipients"
            option-label="name"
            emit-value
            map-options
            :options="unref(recipientsFilter.entities)"
            :options-loading="unref(recipientsFilter.loading)"
            placeholder="Select or enter name"
            clearable
            required
            use-input
            new-value-mode="add"
            :validations="{ required: true }"
            label="Name"
            :disable="isEditMode"
            @update:model-value="handleCommunicationSelection"
            @filter="recipientsFilter.onFilter"
            @filter-abort="recipientsFilter.onFilterAbort"
            @virtual-scroll="recipientsFilter.onScroll"
          />

          <div class="row q-col-gutter-md">
            <div class="col-6">
              <PInput
                v-model="formData.phone"
                placeholder="Enter number"
                maxlength="10"
                :disable="isPhoneDisabled"
                :validations="{
                  type: 'phoneNumber',
                  key: 'phone',
                  label: 'Contact No.',
                  length: 10,
                  required: true,
                }"
                label="Contact No."
              >
                <template #prepend>
                  <span class="text-sm">{{ isdCode }}</span>
                </template>
              </PInput>
            </div>

            <div class="col-6">
              <PInput
                v-model="formData.email"
                placeholder="Enter email"
                label="Email"
                :disable="isEmailDisabled"
                :validations="{
                  type: 'email',
                }"
              />
            </div>
          </div>

          <!-- Actions -->
          <div class="flex justify-end q-mt-xl">
            <PBtn :fluid="false" :loading="isLoading" type="submit">
              {{ submitLabel }}
            </PBtn>
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useQuasar, useDialogPluginComponent } from "quasar";

import {
  getRecipients,
  putRecipients,
  put,
  getTypes,
} from "src/api/communication-settings";

import CloseIcon from "assets/icons/close.svg";
import PConfirmDialog from "src/components/PConfirmDialog.vue";

import communicationFormModel from "src/modules/user/models/communicationForm.js";

// Constants
const ALL_COMM_CODE = "all";
const USER_TYPE = "user";
const ISD_CODE = "+91";

const $q = useQuasar();
const { getCommunicationSettings } = useCommunicationSettingsStore();

defineEmits([...useDialogPluginComponent.emits]);

const isdCode = ref(ISD_CODE);
const isLoading = ref(false);
const recipients = ref(null);

const formData = ref(communicationFormModel()());

const props = defineProps({
  userId: {
    type: String,
    required: true,
  },
  role: {
    type: String,
    required: true,
  },
  recipient: {
    type: Object,
    default: null,
  },
});

const isEditMode = computed(() => !!props.recipient?._id);
const heading = computed(() => (isEditMode.value ? "Edit" : "Add"));
const submitLabel = computed(() => (isEditMode.value ? "Update" : "Submit"));

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

const isPhoneDisabled = computed(
  () => isEditMode.value && props.recipient?.type === USER_TYPE,
);

const isEmailDisabled = computed(() => {
  if (!isEditMode.value) return false;

  const isUser = props.recipient?.type === USER_TYPE;
  const hasEmail = !!props.recipient?.email?.trim();

  return isUser && hasEmail;
});

watchEffect(() => {
  if (isEditMode.value) {
    const {
      recipient: {
        _id,
        name = "",
        phone = "",
        email = "",
        is_custom = false,
        typeData,
      },
    } = props;

    Object.assign(formData.value, {
      type: typeData ? [{ _id: typeData._id, name: typeData.name }] : [],
      recipient_id: _id,
      name,
      phone,
      email,
      is_custom,
    });

    recipients.value = { _id, name };
  } else {
    formData.value = communicationFormModel()();

    recipients.value = null;
  }
});

const communicationTypesFilter = computed(() =>
  useFilter({
    getter: getTypes,
    options: {
      params: { applies_to: props.role },
    },
  }),
);

const recipientsFilter = computed(() => {
  const typeEntities = unref(communicationTypesFilter.value?.entities) || [];

  const selectedCodes = typeEntities
    .filter((t) => formData.value.type.includes(t._id))
    .map((t) => t.code);

  const codeParam = selectedCodes.length ? selectedCodes.join(",") : null;

  return useFilter({
    getter: getRecipients,
    options: {
      params: {
        user_id: props.userId,
        ...(codeParam && { code: codeParam }),
      },
    },
  });
});

const recipientEntities = computed(
  () => unref(recipientsFilter.value.entities) || [],
);

// Reset form fields to initial state
const resetFormFields = () => {
  recipients.value = null;

  Object.assign(formData.value, {
    name: "",
    phone: "",
    email: "",
    recipient_id: null,
    is_custom: false,
  });
};

const handleCommunicationSelection = (val) => {
  if (!val) {
    Object.assign(formData.value, {
      phone: "",
      email: "",
      recipient_id: null,
    });

    return;
  }

  const recipient = recipientEntities.value.find(({ _id }) => _id === val._id);

  if (recipient) {
    const { _id, name = "", phone = "", email = "" } = recipient;

    Object.assign(formData.value, {
      is_custom: false,
      recipient_id: _id,
      name,
      phone,
      email,
    });
  } else {
    Object.assign(formData.value, {
      is_custom: true,
      recipient_id: null,
      name: val,
      phone: "",
      email: "",
    });
  }
};

// Generate payload for API submission
const generatePayload = () => ({
  ...formData.value,
  type: formData.value.type.map((t) => (typeof t === "object" ? t._id : t)),
  user_id: props.userId,
});

// Generate confirmation text based on mode and selected types
const generateConfirmationText = (payload) => {
  if (isEditMode.value) {
    return "Are you sure you want to edit these details?";
  }

  const typeEntities = unref(communicationTypesFilter.value?.entities) || [];
  const typeNames = typeEntities
    .filter((t) => payload.type.includes(t._id))
    .map((t) => t.name);
  const typeList = typeNames.join(", ") || "selected types";

  return `Are you sure you want to add ${formData.value.name || "this contact"} to ${typeList}?`;
};

const handleConfirm = async (payload) => {
  try {
    if (isEditMode.value) {
      await put({
        user_id: props.userId,
        recipient_id: props.recipient._id,
        type_id: props.recipient.typeData?._id,
        ...payload,
      });
    } else {
      await putRecipients(payload);
    }

    await getCommunicationSettings({
      _id: props.userId,
      params: { role: props.role },
    });

    onDialogOK();
  } catch (error) {
    console.error(error);
  }
};

const onSubmit = async () => {
  const payload = generatePayload();
  const confirmationText = generateConfirmationText(payload);

  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      title: isEditMode.value ? "Edit" : "Add",
      text: confirmationText,
      labelClass: "text-left q-ma-xs q-px-xs rounded-borders",
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      cancelLabel: "Cancel",
      closable: true,
    },
  }).onOk(async () => {
    try {
      isLoading.value = true;

      await handleConfirm(payload);
    } finally {
      isLoading.value = false;
    }
  });
};

const allCommId = computed(() => {
  const entities = unref(communicationTypesFilter.value?.entities) || [];

  if (entities.length > 0) {
    const allFromAPI = entities.find((t) => t.code === ALL_COMM_CODE);

    if (allFromAPI) return allFromAPI._id;
  }

  if (isEditMode.value && props.recipient?.typeData) {
    const td = props.recipient.typeData;

    if (td.code === ALL_COMM_CODE) {
      return td._id;
    }
  }

  return null;
});

const handleTypeSelection = (newTypes) => {
  const types = Array.isArray(newTypes) ? newTypes : [];
  const allId = allCommId.value;

  formData.value.type = types.includes(allId)
    ? [allId]
    : types.filter((id) => id !== allId);
};

const processedCommunicationTypes = computed(() => {
  const entities = unref(communicationTypesFilter.value?.entities) || [];

  const selected = formData.value.type || [];
  const selectedIds = selected.map((t) => (typeof t === "object" ? t._id : t));

  const allId = allCommId.value;
  const isAllSelected = selectedIds.includes(allId);
  const isAnyOtherSelected = selectedIds.length > 0 && !isAllSelected;

  return entities.map((opt) => ({
    ...opt,
    disable: isAllSelected
      ? opt._id !== allId
      : isAnyOtherSelected && opt._id === allId,
  }));
});

watch(
  () => formData.value.type,
  (newVal, oldVal) => {
    if (
      !isEditMode.value &&
      oldVal &&
      JSON.stringify(newVal) !== JSON.stringify(oldVal)
    ) {
      resetFormFields();
    }
  },
);
</script>
